import { useCommitSnapshot, useSnapshot, useSnapshotDrc } from "@features/circuits/hooks/useCircuitIr";
import { ApiError } from "@lib/axios";
import type { CommitGateErrorDetail } from "@models/circuit-ir";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";

import { DrcFindingItem } from "./DrcFindingItem";
import { DrcSeverityIcon } from "./DrcSeverityIcon";

interface CommitDraftDialogProps {
  open: boolean;
  snapshotId: string | null;
  /** Project the new `:SchematicVersion` lands in. */
  projectId: string | null;
  /** Target Circuit within the project (Phase 14 multi-Circuit). When
   *  null, backend defaults to the project's `main` circuit. */
  circuitId?: string | null;
  /** Optional parent_version_id — set when the snapshot was seeded from
   *  a specific committed version. */
  parentVersionId?: string | null;
  onClose: () => void;
  onCommitted?: (versionId: string) => void;
}

/**
 * The commit gesture — promotes the working-buffer snapshot into a
 * permanent `:SchematicVersion`. Per IR plan §6.7, commit is the
 * mandatory DRC gate: hard errors block by default, with an explicit
 * "commit anyway" escape hatch.
 *
 * On 422 the backend returns `CommitGateErrorDetail` with the full
 * findings list — we surface the offending findings inline so the user
 * can decide between fixing in chat or bypassing.
 */
export function CommitDraftDialog({
  open,
  snapshotId,
  projectId,
  circuitId,
  parentVersionId,
  onClose,
  onCommitted,
}: CommitDraftDialogProps) {
  // The outer component is always mounted; the body — which owns form
  // state — only mounts while ``open`` is true. MUI Dialog defaults to
  // ``keepMounted=false`` so the inner ``CommitDraftDialogBody``
  // unmounts the moment ``open`` flips false, and the next open
  // remounts with fresh useState initial values. No reset useEffect
  // needed.
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <CommitDraftDialogBody
        snapshotId={snapshotId}
        projectId={projectId}
        circuitId={circuitId}
        parentVersionId={parentVersionId}
        onClose={onClose}
        onCommitted={onCommitted}
      />
    </Dialog>
  );
}

interface CommitDraftDialogBodyProps {
  snapshotId: string | null;
  projectId: string | null;
  circuitId?: string | null;
  parentVersionId?: string | null;
  onClose: () => void;
  onCommitted?: (versionId: string) => void;
}

function CommitDraftDialogBody({
  snapshotId,
  projectId,
  circuitId,
  parentVersionId,
  onClose,
  onCommitted,
}: CommitDraftDialogBodyProps) {
  const { data: snapshot } = useSnapshot(snapshotId);
  const { data: drc } = useSnapshotDrc(snapshotId);
  const commit = useCommitSnapshot();

  const [description, setDescription] = useState("");
  const [semverLabel, setSemverLabel] = useState("");
  const [setCurrent, setSetCurrent] = useState(true);
  const [bypassDrc, setBypassDrc] = useState(false);

  const componentCount = snapshot?.ir.components.length ?? 0;
  const netCount = snapshot?.ir.nets.length ?? 0;

  // 422 detail — surface DRC findings the gate caught. ApiError carries
  // the parsed `detail` string from axios; for the structured 422 path
  // backend serialises a JSON object — try to parse.
  const gateError = useMemo(() => {
    const err = commit.error;
    if (!(err instanceof ApiError) || err.status !== 422) return null;
    try {
      const parsed = JSON.parse(err.message) as CommitGateErrorDetail;
      return parsed;
    } catch {
      return null;
    }
  }, [commit.error]);

  function handleCommit() {
    if (!snapshotId || !projectId) return;
    commit.mutate(
      {
        snapshotId,
        project_id: projectId,
        circuit_id: circuitId ?? undefined,
        parent_version_id: parentVersionId,
        description: description.trim() || undefined,
        semver_label: semverLabel.trim() || undefined,
        set_current: setCurrent,
        drc_gate: !bypassDrc,
      },
      {
        onSuccess: (data) => {
          onCommitted?.(data.version_id);
          onClose();
        },
      },
    );
  }

  const blockingErrors = drc?.error_count ?? 0;

  return (
    <>
      <DialogTitle sx={{ pb: 0.5 }}>
        Commit draft
        {snapshot && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {componentCount} component{componentCount === 1 ? "" : "s"} ·{" "}
            {netCount} net{netCount === 1 ? "" : "s"}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
            placeholder="What changed? Shown in the version history."
          />
          <TextField
            label="Version label (optional)"
            value={semverLabel}
            onChange={(e) => setSemverLabel(e.target.value)}
            placeholder="e.g. v3.2 — defaults to auto-incrementing"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={setCurrent}
                onChange={(e) => setSetCurrent(e.target.checked)}
              />
            }
            label="Make this the current version"
          />

          {drc && (
            <DrcSummaryCard
              errorCount={drc.error_count}
              warningCount={drc.warning_count}
              infoCount={drc.info_count}
            />
          )}

          {blockingErrors > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={bypassDrc}
                  onChange={(e) => setBypassDrc(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Commit anyway (bypass DRC gate)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    The {blockingErrors} hard error
                    {blockingErrors === 1 ? " will" : "s will"} land in the
                    permanent version. Use only when you intend to fix in a
                    follow-up commit.
                  </Typography>
                </Box>
              }
            />
          )}

          {gateError && (
            <Alert severity="error">
              <AlertTitle>Commit blocked by DRC</AlertTitle>
              {gateError.message}
              <Divider sx={{ my: 1 }} />
              <Stack divider={<Divider flexItem />}>
                {gateError.drc_report.findings
                  .filter((f) => f.severity === "error")
                  .slice(0, 5)
                  .map((f, i) => (
                    <DrcFindingItem
                      key={`${f.code}-${i}`}
                      finding={f}
                      ir={snapshot?.ir}
                      snapshotId={snapshotId}
                    />
                  ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={commit.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCommit}
          disabled={
            !snapshotId ||
            !projectId ||
            commit.isPending ||
            (blockingErrors > 0 && !bypassDrc)
          }
          startIcon={
            commit.isPending ? (
              <CircularProgress size={14} color="inherit" />
            ) : undefined
          }
        >
          {commit.isPending ? "Committing…" : "Commit"}
        </Button>
      </DialogActions>
    </>
  );
}

function DrcSummaryCard({
  errorCount,
  warningCount,
  infoCount,
}: {
  errorCount: number;
  warningCount: number;
  infoCount: number;
}) {
  const severity =
    errorCount > 0 ? "error" : warningCount > 0 ? "warning" : "success";
  return (
    <Alert
      severity={severity}
      icon={
        errorCount > 0 ? (
          <DrcSeverityIcon severity="error" />
        ) : warningCount > 0 ? (
          <DrcSeverityIcon severity="warning" />
        ) : undefined
      }
    >
      <AlertTitle>
        {errorCount > 0
          ? `${errorCount} error${errorCount === 1 ? "" : "s"}`
          : warningCount > 0
            ? `${warningCount} warning${warningCount === 1 ? "" : "s"}`
            : "Clean"}
        {errorCount > 0 &&
          warningCount > 0 &&
          `, ${warningCount} warning${warningCount === 1 ? "" : "s"}`}
        {infoCount > 0 && `, ${infoCount} info`}
      </AlertTitle>
      {errorCount > 0
        ? "Errors will block the commit gate by default."
        : "Warnings and info don't block — review them before committing."}
    </Alert>
  );
}
